import { Link } from "react-router-dom";
import type { Topic } from "@/types/news";
import { formatHeat, getCategoryName, statusMeta } from "@/utils/news";

interface TopicCardProps {
  topic: Topic;
  variant?: "grid" | "row";
  rank?: number;
}

export default function TopicCard({ topic, variant = "grid", rank }: TopicCardProps) {
  const status = statusMeta(topic.status);

  if (variant === "row") {
    return (
      <Link
        to={`/topic/${topic.id}`}
        className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
      >
        <div className="relative h-24 w-32 shrink-0 overflow-hidden rounded-md bg-background-100 sm:h-28 sm:w-40">
          <img
            src={topic.cover}
            alt={`${topic.title} 事件配图`}
            title={`${topic.title} - 蝌蚪爆料`}
            className="h-full w-full object-top"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${status.className}`}>
              <i className={`${status.icon} text-xs`} aria-hidden="true"></i>
              {status.label}
            </span>
            <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-800">
              {getCategoryName(topic.category)}
            </span>
          </div>
          <h3 className="mt-2 line-clamp-2 font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-700">
            {topic.title}
          </h3>
          <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">
            {topic.tagline}
          </p>
          <div className="mt-auto flex flex-wrap items-center gap-3 pt-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-sm text-primary-500" aria-hidden="true"></i>
              热度 {formatHeat(topic.heat)}
            </span>
            <span className="hidden items-center gap-1 sm:flex">
              <i className="ri-refresh-line text-sm" aria-hidden="true"></i>
              {topic.updatedAt}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/topic/${topic.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative h-44 w-full overflow-hidden bg-background-100 md:h-48">
        <img
          src={topic.cover}
          alt={`${topic.title} 事件配图`}
          title={`${topic.title} - 蝌蚪爆料`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute left-3 top-3 flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${status.className}`}>
            <i className={`${status.icon} text-xs`} aria-hidden="true"></i>
            {status.label}
          </span>
          {typeof rank === "number" && (
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-foreground-950/70 font-heading text-xs font-bold text-background-50">
              {rank}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <span className="text-[11px] font-medium text-primary-700">{getCategoryName(topic.category)}</span>
        <h3 className="mt-1.5 line-clamp-2 font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
          {topic.title}
        </h3>
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">{topic.summary}</p>
        <div className="mt-auto flex items-center justify-between pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-sm text-primary-500" aria-hidden="true"></i>
            热度 {formatHeat(topic.heat)}
          </span>
          <span>{topic.updatedAt.slice(5, 10)} 更新</span>
        </div>
      </div>
    </Link>
  );
}